import { useContext, useEffect, useState } from "react";
import { TransferContext } from "../../context/TransferContext";
import {
  STEP_DESTINATION_ACCOUNT,
  STEP_DESTINATION_EXPLORE_IRA,
  STEP_DESTINATION_EXPLORE_IRA_1,
  STEP_DESTINATION_EXPLORE_IRA_2,
  STEP_DESTINATION_EXPLORE_IRA_3,
  STEP_DESTINATION_EXPLORE_IRA_LOADING,
  STEP_DESTINATION_EXPLORE_IRA_OPTION_BETTERMENT,
  STEP_DESTINATION_EXPLORE_IRA_OPTION_FUTUREMONEY,
  STEP_DESTINATION_EXPLORE_IRA_OPTION_ROBINHOOD,
  STEP_DESTINATION_EXPLORE_IRA_OPTION_SOFI,
  STEP_DESTINATION_EXPLORE_IRA_OPTIONS,
  STEP_DESTINATION_EXPLORE_IRA_RESULT,
  STEP_DESTINATION_OPEN_AND_CONNECT_IRA,
} from "../../class/transferDetails";
import { TransferOptionValues } from "../../utils/getTransferOptionContent";
import {
  AdditionalFeature,
  Feature,
  HelpPreference,
  iraItemIds,
  IRASurveyData,
  NONE,
  roboIRAItemIds,
} from "../../class/iraQuestionnaire";
import DestinationAccount, { AccountType } from "../../class/destinationAccount";
import { ModalContainerContentHeader, ModalContainerCTAButtonDiv } from "../general/modal/styles";
import { BodyLargeRegular, TitleLarge } from "../../components/font";
import { Button } from "@manifesthhqinc/ui-library";

export const getAccountOpeningUrl = (providerName: string): string => {
  switch (providerName) {
    case "Betterment":
      return "https://betterment.com";
    case "Robinhood":
      return "https://www.robinhood.com";
    case "SoFi":
      return "https://www.sofi.com/invest/automated/";
    case "Vanguard":
      return "https://open-account.web.vanguard.com/ona-open-account/account-selection/account-selector?accounts=retirement";
    case "WealthFront":
      return "https://www.wealthfront.com/start/account-type?intent=home";
    case "Fidelity":
      return "https://www.fidelity.com/retirement-ira/overview";
    case "Charles Schwab":
      return "https://www.schwab.com/open-an-account";
    default:
      return "";
  }
};

export type UseExploreIRAOptionsType = ReturnType<typeof useExploreIRAOptions>;

const useExploreIRAOptions = () => {
  const {
    currentScreen,
    goToScreen,
    isLoading,
    setIsLoading,
    userSelectedTransferOption,
    fetchIRARecommendation,
    iraRecommendationContent,
    createDestainationInAPI,
    setShowModal,
    setModalContent,
  } = useContext(TransferContext);
  const [featurePriority, setFeaturePriority] = useState<Feature[]>(
    userSelectedTransferOption === TransferOptionValues.managedIRA ? roboIRAItemIds : iraItemIds,
  );
  const [iraQuestionnaireData, setIRAQuestionnaireData] = useState<IRASurveyData>({});
  const [extraFeatures, setExtraFeatures] = useState<AdditionalFeature[]>([]);
  const [formValidation, setFormValidation] = useState<Record<string, string>>({});
  const onClickStart = () => {
    goToScreen(STEP_DESTINATION_EXPLORE_IRA_1);
  };

  useEffect(() => {
    setFormValidation({});
  }, [iraQuestionnaireData, extraFeatures]);

  const sendDataToAPI = (data?: IRASurveyData) => {
    const updatedData: IRASurveyData = {
      ...data,
      featurePreference: {
        FIRST: featurePriority[0],
        SECOND: featurePriority[1],
        THIRD: featurePriority[2],
        FOURTH: featurePriority[3],
        FIFTH: featurePriority[4],
      },
      extras: extraFeatures,
    };
    setIRAQuestionnaireData(updatedData);
    fetchIRARecommendation(
      updatedData,
      userSelectedTransferOption as TransferOptionValues.ira | TransferOptionValues.managedIRA,
    );
  };

  const onClickContinue = (data?: IRASurveyData) => {
    switch (currentScreen) {
      case STEP_DESTINATION_EXPLORE_IRA_1:
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_2);
        break;
      case STEP_DESTINATION_EXPLORE_IRA_2:
        // check the user input
        if (extraFeatures.length === 0) {
          setFormValidation({
            extras: "Please provide your selection",
          });
          return;
        }
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_3);
        break;
      case STEP_DESTINATION_EXPLORE_IRA_3:
        sendDataToAPI(data);
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_LOADING);
        break;
      case STEP_DESTINATION_EXPLORE_IRA_RESULT:
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_OPTIONS);
        break;
      default:
        break;
    }
  };

  const updateExtraFeature = (feature: AdditionalFeature) => {
    if (feature === NONE && !extraFeatures.includes(NONE)) {
      setExtraFeatures([NONE]);
    } else if (extraFeatures.includes(feature)) {
      setExtraFeatures(extraFeatures.filter((e) => e !== feature));
    } else {
      setExtraFeatures([...extraFeatures, feature]);
    }
  };

  const updateHelpFeature = (supportModel: HelpPreference) => {
    setIRAQuestionnaireData({
      ...iraQuestionnaireData,
      supportModel,
    });
    onClickContinue({ ...iraQuestionnaireData, supportModel });
  };

  const showResult = () => {
    setIsLoading(false);
    goToScreen(STEP_DESTINATION_EXPLORE_IRA_RESULT);
  };

  const onClickRestart = () => {
    setModalContent(
      <>
        <ModalContainerContentHeader>
          <TitleLarge>Restart these questions?</TitleLarge>
          <BodyLargeRegular>Answer Henry’s questions again for an updated IRA recommendation.</BodyLargeRegular>
        </ModalContainerContentHeader>
        <ModalContainerCTAButtonDiv>
          <Button onClick={onClickConfirmRestart} variant="secondary">
            Yes
          </Button>
          <Button onClick={() => setShowModal(false)}>No</Button>
        </ModalContainerCTAButtonDiv>
      </>,
    );
    setShowModal(true);
  };

  const onClickConfirmRestart = () => {
    setIRAQuestionnaireData({});
    goToScreen(STEP_DESTINATION_EXPLORE_IRA);
    setShowModal(false);
  };

  const onClickOpenAccount = async (providerName: string) => {
    await createDestainationInAPI(
      new DestinationAccount({
        destinationAccountType: AccountType.ira,
        providerName,
        yetToOpen: true,
        statementSkipped: "TRUE",
        accountOpeningUrl: getAccountOpeningUrl(providerName),
      }),
    );

    if (providerName === "Futuremoney") {
      // Set destination details to transfer details
      goToScreen(STEP_DESTINATION_ACCOUNT);
    } else {
      goToScreen(STEP_DESTINATION_OPEN_AND_CONNECT_IRA);
    }
  };

  const onClickLearnMore = (providerName: string) => {
    switch (providerName) {
      case "Futuremoney":
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_OPTION_FUTUREMONEY);
        break;
      case "Betterment":
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_OPTION_BETTERMENT);
        break;
      case "Robinhood":
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_OPTION_ROBINHOOD);
        break;
      case "SOFI":
        goToScreen(STEP_DESTINATION_EXPLORE_IRA_OPTION_SOFI);
        break;
    }
  };

  return {
    currentScreen,
    isLoading,
    setIsLoading,
    onClickStart,
    userSelectedTransferOption,
    setFeaturePriority,
    featurePriority,
    onClickContinue,
    iraQuestionnaireData,
    setIRAQuestionnaireData,
    extraFeatures,
    updateExtraFeature,
    updateHelpFeature,
    showResult,
    iraRecommendationContent,
    onClickRestart,
    onClickOpenAccount,
    onClickLearnMore,
    goToScreen,
    formValidation,
  };
};

export default useExploreIRAOptions;
